// Acoustic furniture lookup shared by web + mobile serialize.
// Visual GLB paths stay in app-specific catalogs; wire material/kind live here.
//
// Every entry mirrors one FURNITURE_PRESETS row in @roomtreatment/room-flow.
// Nothing but web/tests/unit/furnitureAcousticsParity.test.ts holds the two
// in step — api-client cannot import room-flow (neither depends on the
// other), so adding a preset there means adding it here too.

export type FurnitureKind = 'solid' | 'floorPatch' | 'decor';

export interface FurnitureAcoustic {
  /** Absent on `kind: 'decor'` — it reaches no absorption path at all. */
  absorptionMaterial?: string;
  kind: FurnitureKind;
  /** Mirrors `FURNITURE_PRESETS[id].category === 'Seating'` — deciding what
   *  counts as a seat is room-flow's/the client's call (preset category),
   *  same split `kind` already follows for what to do with the item
   *  physically. Wired onto `AdminFurniture.seat` by `furnitureToWire`.
   *  Absent (⇒ false) for every non-seating preset, same convention as
   *  `absorptionMaterial` on `kind: 'decor'`. */
  seat?: boolean;
}

export const FURNITURE_ACOUSTICS: Record<string, FurnitureAcoustic> = {
  "bedSingle": { absorptionMaterial: "upholstered", kind: "solid" },
  "bedDouble": { absorptionMaterial: "upholstered", kind: "solid" },
  "bedBunk": { absorptionMaterial: "upholstered", kind: "solid" },
  "loungeSofa": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "loungeSofaLong": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "loungeSofaCorner": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "loungeSofaOttoman": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "loungeChair": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "loungeChairRelax": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "loungeDesignChair": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "benchCushion": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "chairCushion": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "chair": { absorptionMaterial: "wood_cabinet", kind: "solid", seat: true },
  "stoolBar": { absorptionMaterial: "wood_cabinet", kind: "solid", seat: true },
  "desk": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "deskCorner": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "table": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "tableRound": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "tableCoffee": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "tableCoffeeGlass": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "tableGlass": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "sideTable": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "bookcaseClosed": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "bookcaseClosedWide": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "bookcaseOpen": { absorptionMaterial: "bookshelf_filled", kind: "solid" },
  "bookcaseOpenLow": { absorptionMaterial: "bookshelf_filled", kind: "solid" },
  "cabinetTelevision": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "sideTableDrawers": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "coatRackStanding": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "kitchenFridge": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "kitchenFridgeLarge": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "kitchenFridgeSmall": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "kitchenStove": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "kitchenSink": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "kitchenMicrowave": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "kitchenCabinet": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "kitchenCabinetUpper": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "hoodModern": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "toilet": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "bathtub": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "shower": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "bathroomSink": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "bathroomCabinet": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "washer": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "dryer": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "washerDryerStacked": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "televisionModern": { absorptionMaterial: "hard_reflective", kind: "solid" },
  "rugRectangle": { absorptionMaterial: "rug_carpet", kind: "floorPatch" },
  "rugRounded": { absorptionMaterial: "rug_carpet", kind: "floorPatch" },
  "rugRound": { absorptionMaterial: "rug_carpet", kind: "floorPatch" },
  "rugSquare": { absorptionMaterial: "rug_carpet", kind: "floorPatch" },
  "rugSquareBlue": { absorptionMaterial: "rug_carpet", kind: "floorPatch" },
  "rugDoormat": { absorptionMaterial: "rug_carpet", kind: "floorPatch" },
  "bedDoubleV2": { absorptionMaterial: "upholstered", kind: "solid" },
  "bedKing": { absorptionMaterial: "upholstered", kind: "solid" },
  "bedSingleV2": { absorptionMaterial: "upholstered", kind: "solid" },
  "sofaLarge": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "couch": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "couchTwo": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "couchThree": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "sofaSectionalL": { absorptionMaterial: "upholstered", kind: "solid", seat: true },
  "coffeeTableV2": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "deskV2": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "bookcaseTall": { absorptionMaterial: "bookshelf_filled", kind: "solid" },
  "bookshelfNarrow": { absorptionMaterial: "bookshelf_filled", kind: "solid" },
  "bookshelfWide": { absorptionMaterial: "bookshelf_filled", kind: "solid" },
  "bookshelfWideTwo": { absorptionMaterial: "bookshelf_filled", kind: "solid" },
  "cabinetTall": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "credenza": { absorptionMaterial: "wood_cabinet", kind: "solid" },
  "plantSmall1": { kind: "decor" },
  "plantSmall2": { kind: "decor" },
  "plantSmall3": { kind: "decor" },
  "pottedPlant": { kind: "decor" },
  "plantSmall4": { kind: "decor" },
  "pottedPlantMedium": { kind: "decor" },
  "pottedPlantLarge": { kind: "decor" },
};

export function furnitureAcoustic(presetId: string): FurnitureAcoustic | undefined {
  return FURNITURE_ACOUSTICS[presetId];
}
